import { useEffect, useMemo } from 'react'

import avatarPlaceholderSrc from '@/shared/assets/img/avatarPlaceholder.png'
import { UploadOutlined } from '@ant-design/icons'
import { Avatar, Button, Flex, Spin, Upload } from 'antd'
import CropperImage from 'antd-cropper-img'
import { useUnit } from 'effector-react'

import { type UploadFileWithId, createSingleUploadModel } from '../model'

type UploadAvatarProps = {
  id?: string
  accept?: string
  value?: UploadFileWithId | null
  onChange?: (fileList: UploadFileWithId | null) => void
}

export function UploadAvatar({
  id,
  accept = 'image/jpeg,image/png',
  value,
  onChange,
}: UploadAvatarProps) {
  const uploadModel = useMemo(() => createSingleUploadModel(), [])
  const [fileStore, initFile, addFile, removeFile] = useUnit([
    uploadModel.$file,
    uploadModel.fileInit,
    uploadModel.fileAdded,
    uploadModel.fileRemoved,
  ])
  const UploadModelGate = uploadModel.Gate

  useEffect(() => {
    if (fileStore.file !== value) {
      if (value === null) {
        onChange?.(null)
      } else if (fileStore.file) {
        onChange?.(fileStore.file)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStore.file, value])

  useEffect(() => {
    if (value && fileStore.file === null) {
      initFile(value)
    } else if (!value && fileStore.file) {
      removeFile()
    }
  }, [value, initFile, removeFile, fileStore.file])

  const isPending =
    fileStore.file?.status === 'uploading' ||
    Boolean(fileStore.file && !fileStore.file.url)
  const src = (value && value.url) ?? avatarPlaceholderSrc

  return (
    <Flex vertical align="center">
      <UploadModelGate />
      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <Spin spinning={isPending}>
          <Avatar src={src} size={144} />
        </Spin>
      </div>
      <CropperImage modalTitle="Загрузка аватара">
        <Upload
          id={id}
          pastable
          name="avatar"
          accept={accept}
          maxCount={1}
          fileList={value ? [value] : []}
          onChange={(e) => {
            if (e.fileList.length > 0) {
              onChange?.(e.fileList[0])
            }
            return false
          }}
          beforeUpload={(file) => {
            addFile(file)
            return false
          }}
          onRemove={() => {
            removeFile()
            return false
          }}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Изменить аватар</Button>
        </Upload>
      </CropperImage>
    </Flex>
  )
}
